import { ApiService } from './api';
import { ApiResponse } from '../types/auth';
import {
  ClientResponse,
  ClientsRequest,
  SendMessageToClientRequest,
  ChangeClientStatusRequest,
  ClientOrdersResponse,
  ClientsNotificationRequest,
  AdminClientsPageResponse
} from '../types/clients';

export class ClientsService {
  private apiService: ApiService;

  constructor() {
    this.apiService = ApiService.getInstance();
  }

  async getClients(request: ClientsRequest): Promise<ApiResponse<AdminClientsPageResponse>> {
    try {
      const params = new URLSearchParams({
        searchQuery: request.searchQuery,
        page: request.page.toString(),
        pageSize: request.pageSize.toString(),
      });

      const appendParam = (key: string, value: string | number | boolean | undefined | null) => {
        if (value === undefined || value === null || value === '') {
          return;
        }
        params.append(key, value.toString());
      };

      if (request.status) {
        params.append('status', request.status);
      }

      appendParam('hasOrders', request.hasOrders);
      appendParam('onlyCancelledOrders', request.onlyCancelledOrders);
      appendParam('lastCompletedOrderBefore', request.lastCompletedOrderBefore);
      appendParam('lastCompletedOrderAfter', request.lastCompletedOrderAfter);
      appendParam('lastCompletedOrderBetweenStart', request.lastCompletedOrderBetweenStart);
      appendParam('lastCompletedOrderBetweenEnd', request.lastCompletedOrderBetweenEnd);
      appendParam('lastCompletedOrderWithinDays', request.lastCompletedOrderWithinDays);
      appendParam('minCompletedOrders', request.minCompletedOrders);
      appendParam('maxCompletedOrders', request.maxCompletedOrders);
      appendParam('createdAtFrom', request.createdAtFrom);
      appendParam('createdAtTo', request.createdAtTo);

      if (request.orderStatusIn && request.orderStatusIn.length > 0) {
        params.append('orderStatusIn', request.orderStatusIn.join(','));
      }

      if (request.platform) {
        params.append('platform', request.platform);
      }

      const response = await this.apiService.get<AdminClientsPageResponse>(
        `/admin/clients?${params.toString()}`
      );

      return {
        data: response,
        success: true
      };
    } catch (error) {
      return {
        error: error instanceof Error ? error.message : 'Failed to fetch clients',
        success: false
      };
    }
  }

  async getClient(clientId: number): Promise<ApiResponse<ClientResponse>> {
    try {
      const response = await this.apiService.get<ClientResponse>(
        `/admin/clients/${clientId}`
      );

      return {
        data: response,
        success: true
      };
    } catch (error) {
      return {
        error: error instanceof Error ? error.message : 'Failed to fetch client details',
        success: false
      };
    }
  }

  async sendMessageToClient(request: SendMessageToClientRequest): Promise<ApiResponse<void>> {
    try {
      await this.apiService.post<void>(
        '/admin/clients/send-message',
        request
      );

      return {
        success: true
      };
    } catch (error) {
      return {
        error: error instanceof Error ? error.message : 'Failed to send message',
        success: false
      };
    }
  }

  async changeClientStatus(request: ChangeClientStatusRequest): Promise<ApiResponse<void>> {
    try {
      await this.apiService.post<void>(
        '/admin/clients/change-status',
        request
      );

      return {
        success: true
      };
    } catch (error) {
      return {
        error: error instanceof Error ? error.message : 'Failed to change client status',
        success: false
      };
    }
  }

  async getClientOrders(clientId: number, page: number, pageSize: number): Promise<ApiResponse<ClientOrdersResponse>> {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        pageSize: pageSize.toString(),
      });

      const response = await this.apiService.get<any>(
        `/admin/clients/${clientId}/orders?${params.toString()}`
      );

      // Backend returns array directly - wrap it in response object
      const safeResponse: ClientOrdersResponse = {
        orders: Array.isArray(response) ? response : [],
        totalPages: 0,
        totalElements: 0,
        currentPage: page
      };

      return {
        data: safeResponse,
        success: true
      };
    } catch (error) {
      return {
        error: error instanceof Error ? error.message : 'Failed to fetch client orders',
        success: false
      };
    }
  }

  async sendClientsNotification(request: ClientsNotificationRequest): Promise<ApiResponse<number>> {
    try {
      const response = await this.apiService.post<number>(
        '/admin/clients/notifications',
        request
      );

      return {
        data: response,
        success: true
      };
    } catch (error) {
      return {
        error: error instanceof Error ? error.message : 'Failed to send notification',
        success: false
      };
    }
  }
}

export const clientsService = new ClientsService();
